import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/shadcn/card';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Alert, AlertTitle, AlertDescription } from '@/components/shadcn/alert';
import { AlertCircle } from 'lucide-react';
import { post } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

// The lead doc is written by Zapier server-side, which can lag a few seconds behind the
// parent landing here. So a `pending` response is retried for a short window before we
// conclude the email was mistyped. ~12s total (6 × 2s).
const MAX_ATTEMPTS = 6;
const POLL_MS = 2000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const looksLikeEmail = (v) => /\S+@\S+\.\S+/.test(v);

function StartTrial() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  // 'idle' | 'loading' | 'use_login' | 'not_found'
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    const trimmed = email.trim();
    setError('');
    if (!looksLikeEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      let res;
      try {
        res = await post('/lead-capture/exchange', { email: trimmed });
      } catch (err) {
        console.error('Lead exchange failed', err);
        setStatus('idle');
        setError('Something went wrong. Please try again.');
        return;
      }

      if (res?.status === 'ok' && res.token && res.userData) {
        Cookies.set('token', res.token, { expires: 7 });
        login(res.userData);
        navigate('/onboarding');
        return;
      }
      if (res?.status === 'use_login') {
        setStatus('use_login');
        return;
      }
      // pending — wait and retry (unless this was the last attempt)
      if (attempt < MAX_ATTEMPTS - 1) {
        await sleep(POLL_MS);
      }
    }
    setStatus('not_found');
  };

  const loading = status === 'loading';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex flex-col items-center justify-center min-h-screen p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Start your free trial</CardTitle>
            <CardDescription>
              Confirm the email you used on Facebook to set up your child's trial.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-center py-6 text-gray-600">Loading your information…</div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status !== 'idle') setStatus('idle');
                      setError('');
                    }}
                    placeholder="you@example.com"
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                  />
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {status === 'not_found' && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>We couldn't find your details</AlertTitle>
                    <AlertDescription>
                      Check it's the same email you entered on Facebook, and try again.
                    </AlertDescription>
                  </Alert>
                )}

                {status === 'use_login' && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>You already have an account</AlertTitle>
                    <AlertDescription>
                      Please <Link to="/login" className="underline font-medium">log in</Link> to continue.
                    </AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={handleConfirm}
                  disabled={loading}
                  className="w-full py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                >
                  Confirm
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default StartTrial;
